/**
 * El informe de lectura: lo único del Studio que habla del LIBRO y no del uso.
 *
 * Todo lo demás que se puede pintar —llamadas, gasto, tokens— dice cómo se ha usado la
 * herramienta. Esto dice cómo está la novela: una nota por pilar, el mapa de capítulos y los
 * problemas con su cita literal, para poder ir a buscarlos en el texto.
 *
 * Dos reglas que gobiernan la pantalla:
 *   · Cuesta dinero del autor. Se avisa ANTES, se pide una vez y el resultado se guarda: volver
 *     a esta pantalla no vuelve a pagarlo.
 *   · Si no hay informe, no se inventa un gráfico. Se dice qué hace falta.
 */
import { useEffect, useState } from "react";
import { capitulosDeTexto, documento, editarPasada, informeGlobal, ultimoInforme, miles } from "../api";
import type { Capitulo, Issue, PlanLibro, Trabajo, Fichero, Informe as Datos } from "../api";
import { manuscritoPropuesto, nombreDeRuta, pasajeEnLibro, tituloDeCapitulo } from "../logica";
import type { Pasaje } from "../logica";

/** Las cinco de siempre, para los informes hechos antes de que el tipo de obra trajera las suyas. */
const PILARES: { id: string; nombre: string }[] = [
  { id: "desarrollo", nombre: "Desarrollo" }, { id: "estilo", nombre: "Estilo" },
  { id: "estructura", nombre: "Estructura" }, { id: "tema", nombre: "Tema" },
  { id: "personajes", nombre: "Personajes" },
];

/** El título llega tal cual sale del manuscrito: «# Capítulo 1 — La carta». La almohadilla es
 *  del Markdown, no del libro. */

const guardado = (dir: string) => `hi.informe:${dir}`;   // respaldo local por si el motor no está

export default function Informe(
  { ficheros, carpeta, doc, pulso, trabajo, alLanzar, plan = null, alCambioPlan, alIr }: {
    ficheros: Fichero[]; carpeta: string; doc?: string | null; pulso?: number;
    trabajo?: Trabajo | null; alLanzar?: () => void;
    // T-33: un hallazgo tiene que llevar a algún sitio — al pasaje, y al plan como pasada.
    plan?: PlanLibro | null; alCambioPlan?: () => void; alIr?: (seccion: string) => void;
  },
) {
  const [datos, setDatos] = useState<Datos | null>(null);
  const [cual, setCual] = useState("");
  const [desde, setDesde] = useState(0);   // marca de tiempo real: el navegador frena los contadores
  const [seg, setSeg] = useState(0);
  const [fallo, setFallo] = useState<string | null>(null);
  // 18-sep (M-01): el tipo de obra es del PROYECTO, no de esta pantalla. Aquí solo se dice cuando
  // falta, porque sin él el motor no lee el libro: con vara equivocada el informe miente.
  const [sinTipo, setSinTipo] = useState(false);

  // MANDA LO QUE EL AUTOR SEÑALÓ. Adivinar por tamaño solo vale si no señaló nada: en una carpeta
  // con muchos documentos —Descargas, por ejemplo— el más grande no es su libro, y aquí la
  // adivinanza no es un detalle estético, porque este botón gasta su dinero analizando lo que diga.
  const mayor = manuscritoPropuesto(ficheros, doc);
  const elegido = ficheros.find((f) => f.path === cual) ?? mayor;

  // Primero el disco —donde vive junto al libro—, y solo si no hay motor, la copia del navegador.
  useEffect(() => {
    if (!carpeta) return;
    let vivo = true;
    ultimoInforme(carpeta, doc)
      .then((d) => { if (vivo && d) setDatos(d); })
      .catch(() => {})
      .finally(() => {
        if (!vivo) return;
        setDatos((actual) => {
          if (actual) return actual;
          const g = localStorage.getItem(guardado(carpeta));
          try { return g ? JSON.parse(g) : null; } catch { return null; }
        });
      });
    return () => { vivo = false; };
  }, [carpeta, doc, pulso]);

  // El reloj se calcula contra la hora de inicio, no sumando segundos: una pestaña en segundo
  // plano frena los temporizadores y el informe parecía durar un tercio de lo que dura.
  useEffect(() => {
    if (!desde) return;
    const t = setInterval(() => setSeg(Math.round((Date.now() - desde) / 1000)), 1000);
    return () => clearInterval(t);
  }, [desde]);

  async function analizar() {
    if (!elegido) return;
    setDesde(Date.now()); setSeg(0); setFallo(null);
    try {
      const doc = await documento(elegido.path);
      if ("error" in doc) throw new Error(doc.error);
      const otros = ficheros.filter((f) => f.path !== elegido.path).slice(0, 6).map((f) => f.path);
      const r = await informeGlobal(doc.text, carpeta, otros, elegido.path);
      if ("trabajo" in r) {
        // En segundo plano: el progreso sale arriba y el informe llega solo al terminar (pulso).
        alLanzar?.();
        return;
      }
      setDatos(r);
      localStorage.setItem(guardado(carpeta), JSON.stringify(r));
    } catch (e) {
      const m = e instanceof Error ? e.message : "El informe no se pudo terminar.";
      if (/novela, relatos, ensayo/.test(m) || /hay que saber su género/.test(m)) setSinTipo(true);
      setFallo(m);
    } finally {
      setDesde(0);
    }
  }

  // El texto del libro y sus capítulos, SOLO cuando hacen falta: localizar un pasaje no debería
  // costar una lectura del manuscrito al abrir la pantalla.
  const [libro, setLibro] = useState<{ texto: string; capitulos: Capitulo[] } | null>(null);
  const [abriendo, setAbriendo] = useState(false);
  const [verPasaje, setVerPasaje] = useState<string | null>(null);
  const [alPlan, setAlPlan] = useState<{ clave: string; estado: "yendo" | "hecho" | string } | null>(null);
  const [informeEntero, setInformeEntero] = useState(false);

  useEffect(() => { setLibro(null); setVerPasaje(null); }, [doc]);

  async function traerLibro() {
    if (libro || !doc) return libro;
    setAbriendo(true);
    try {
      const d = await documento(doc);
      if ("error" in d) throw new Error(d.error);
      const caps = await capitulosDeTexto(d.text, carpeta, doc).catch(() => null);
      const l = { texto: d.text, capitulos: caps?.capitulos ?? [] };
      setLibro(l);
      return l;
    } catch { return null; } finally { setAbriendo(false); }
  }

  const pasajeDe = (i: Issue): Pasaje | null =>
    libro ? pasajeEnLibro(libro.texto, i.excerpt, libro.capitulos.map((c) => ({ title: c.title, texto: (c as unknown as { texto?: string }).texto ?? "" }))) : null;

  /** El hallazgo, convertido en pasada del plan: su capítulo y el arreglo como criterio de cierre. */
  async function añadirAlPlan(clave: string, i: Issue) {
    const l = libro ?? await traerLibro();
    const p = l ? pasajeEnLibro(l.texto, i.excerpt, l.capitulos.map((c) => ({ title: c.title, texto: (c as unknown as { texto?: string }).texto ?? "" }))) : null;
    setAlPlan({ clave, estado: "yendo" });
    try {
      await editarPasada(carpeta, {
        accion: "anadir",
        titulo: i.problema.slice(0, 150),
        capitulos: p?.capitulo ? [p.capitulo] : [1],
        criterio_cierre: i.arreglo.slice(0, 280),
      }, doc);
      setAlPlan({ clave, estado: "hecho" });
      alCambioPlan?.();
    } catch (e) {
      setAlPlan({ clave, estado: (e as Error).message });
    }
  }

  const d = datos?.data ?? null;
  const caps = datos?.chapters ?? [];
  const masLargo = Math.max(1, ...caps.map((c) => c.palabras));
  const issues = d?.issues ?? [];

  return (
    <>
      <section className="carta">
        <h2>Informe de lectura
          {/* 21-sep (Rais): con el informe en marcha decía a la vez «aún no lo has pedido» y
              «leyendo tu manuscrito…». Mientras se lee, la etiqueta no dice nada que contradiga. */}
          <span className="sub">{trabajo?.estado === "en_curso" || desde > 0 ? "en marcha"
            : datos ? `${datos.chapterCount} capítulos analizados` : "aún no lo has pedido"}</span>
        </h2>

        {!datos && !desde && trabajo?.estado !== "en_curso" && (
          <>
            <p className="vacio">
              Un lector profesional se lee tu novela entera, capítulo a capítulo, y devuelve una
              nota por pilar, el mapa del libro y los problemas concretos con su cita literal.
            </p>
            {!ficheros.length
              ? <p className="vacio">Elige antes la carpeta del manuscrito.</p>
              : (
                <>
                {sinTipo && (
                  <div className="aviso">
                    <span>Antes hay que decir qué es tu libro —novela, relatos, ensayo, memorias, manual o guion— y, si es ficción, su género. Con la vara equivocada el informe miente.</span>
                    {alIr && <button className="btn fantasma" onClick={() => alIr("proyectos")}>Decirlo en el proyecto</button>}
                  </div>
                )}
                <div className="dir">
                  <select className="escribir" value={elegido?.path ?? ""} onChange={(e) => setCual(e.target.value)}>
                    {ficheros.map((f) => (
                      <option key={f.path} value={f.path}>{f.name} · {Math.round(f.size / 1024)} KB</option>
                    ))}
                  </select>
                  <button className="btn" onClick={analizar}>Analizar el manuscrito</button>
                </div>
                </>
              )}
            <p className="vacio">
              Tarda varios minutos y se paga con tu llave de OpenRouter: una llamada por capítulo y
              una final. Se guarda al terminar, así que solo se hace una vez por versión.
            </p>
          </>
        )}

        {trabajo?.estado === "en_curso" && (
          <p className="vacio">
            Leyendo tu manuscrito en segundo plano{trabajo.progreso ? `: ${trabajo.progreso.fase ?? `capítulo ${trabajo.progreso.hecho} de ${trabajo.progreso.total}`}` : "…"}.
            Puedes cerrar el Studio o seguir con otra cosa: el informe aparece aquí al terminar.
          </p>
        )}
        {trabajo?.estado === "fallido" && (
          <p className="vacio" style={{ color: "var(--acc)" }}>
            El último informe no se pudo terminar: {trabajo.error}
            {trabajo.reanudable && " Lo ya leído queda guardado: si lo vuelves a pedir con el mismo modelo, sigue donde estaba y no se vuelve a pagar. Con otro modelo empieza de cero."}
          </p>
        )}
        {/* Un reinicio del motor (una actualización, apagar el Mac) corta la lectura: se dice, y si ese
            motor guardaba lo leído, que relanzarla sigue donde estaba (27-sep). */}
        {trabajo?.estado === "interrumpido" && (
          <p className="vacio" style={{ color: "var(--acc)" }}>
            La lectura se cortó{trabajo.progreso ? ` en el paso ${Math.min(trabajo.progreso.hecho + 1, trabajo.progreso.total)} de ${trabajo.progreso.total}` : ""}: el motor se cerró o se reinició mientras leía.{" "}
            {trabajo.reanudable
              ? "Lo ya leído queda guardado: vuelve a pedirla con el mismo modelo y seguirá donde estaba, sin volver a pagar lo leído. Con otro modelo empieza de cero."
              : "Vuelve a pedirla: con este motor empieza de nuevo."}
          </p>
        )}
        {trabajo?.estado === "hecho" && trabajo.resultado?.reanudado === true && (
          <p className="vacio">Esta lectura siguió donde se había cortado: {String(trabajo.resultado.reusadas ?? 0)} partes ya pagadas no se han vuelto a cobrar.</p>
        )}
        {/* Equipo rojo 1.6.30: con partes fallidas la lectura no se da por cerrada; pedirla otra vez pide solo esas. */}
        {trabajo?.estado === "hecho" && Number(trabajo.resultado?.fallidas ?? 0) > 0 && (
          <p className="vacio" style={{ color: "var(--acc)" }}>
            {Number(trabajo.resultado?.fallidas) === 1 ? "Una parte no se pudo leer" : `${String(trabajo.resultado?.fallidas)} partes no se pudieron leer`} y
            el informe lo dice donde toca. Si lo vuelves a pedir con el mismo modelo, solo se pide eso y lo que depende de ello:
            lo ya leído no se vuelve a pagar.
          </p>
        )}
        {desde > 0 && trabajo?.estado !== "en_curso" && (
          <p className="vacio">
            Leyendo tu manuscrito… {Math.floor(seg / 60)}:{String(seg % 60).padStart(2, "0")}.
            Un capítulo cada vez; no cierres el Studio.
          </p>
        )}

        {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}

        {/* R-19: volver a diagnosticar la versión actual, para ver cómo se mueve la nota tras las pasadas. */}
        {datos && !desde && trabajo?.estado !== "en_curso" && !!elegido && (
          <div className="botones">
            <button className="btn fantasma" onClick={analizar}>Volver a analizar {elegido.name}</button>
            <span className="vacio">Tras una pasada: la nota de cada versión aparece en la curva de Pasadas.</span>
          </div>
        )}
        {d && (
          <>
            {d.bps == null
              ? <p className="vacio">Leído como <b>{d.tipo ?? "libro"}</b>: sin BPS, que es de ficción de mercado. La nota es la media de sus seis dimensiones.</p>
              : <div className="cifra"><b>{d.bps ?? "—"}</b><span>BPS · potencial de venta sobre 100</span></div>}
            {datos?.savedPath && (
              <p className="vacio">
                Está en tu carpeta como <b style={{ color: "var(--ink)" }}>
                  {datos.savedPath.split(/[/\\]/).pop()}</b>
                <br />{datos.savedPath}
              </p>
            )}
          </>
        )}
      </section>

      {/* EL INFORME. Estaba descargándose y tirándose: `narrative` no aparecía ni una vez en este
          fichero, así que de un informe de 10.931 caracteres el autor veía una nota y una ruta.
          Lo dijo con estas palabras: «hice el informe de lectura y ha desaparecido». */}
      {datos?.narrative && (
        <section className="carta">
          <h2>El informe
            <span className="sub">{miles(datos.narrative.length)} caracteres</span>
          </h2>
          {/* T-33: era un muro. Lo que se lee primero es qué arreglar; el informe entero, al abrirlo. */}
          {informeEntero
            ? <>
                <div className="hoja larga">{datos.narrative}</div>
                <div className="botones"><button className="btn texto" onClick={() => setInformeEntero(false)}>Plegar el informe</button></div>
              </>
            : <div className="botones">
                <button className="btn fantasma" onClick={() => setInformeEntero(true)}>Leer el informe entero</button>
                <span className="vacio">{datos.savedPath ? "También está en tu carpeta como documento." : ""}</span>
              </div>}
        </section>
      )}

      {d?.scores && (
        <section className="carta">
          {/* M-02: las dimensiones son las del TIPO de obra y vienen en el informe. La pantalla
              pinta lo que llega; solo cae a las cinco de siempre con informes anteriores. */}
          <h2>{d.dimensiones?.length ? "Las seis dimensiones" : "Los cinco pilares"}
            <span className="sub">sobre 10{d.tipo ? ` · ${d.tipo}` : ""}</span>
          </h2>
          <div className="notas">
            {(d.dimensiones?.length ? d.dimensiones : PILARES).filter((x) => typeof d.scores?.[x.id] === "number").map((x) => {
              const v = d.scores?.[x.id] as number;
              return (
                <div className="nota" key={x.id}>
                  <span className="et">{x.nombre}</span>
                  <span className="raya"><i style={{ width: `${v * 10}%` }} /></span>
                  <b className="mono">{v.toLocaleString("es-ES", { maximumFractionDigits: 1 })}</b>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* M-12: lo que se consideró y NO se marcó. Leerlo vale tanto como la lista de lo que sí:
          es lo que dice que el lector se contuvo en vez de llenar la pantalla. */}
      {!!d?.descartados?.length && (
        <section className="carta">
          <h2>Lo que se consideró y no se marcó<span className="sub">{d.descartados.length} · contención, no olvido</span></h2>
          <div className="notas">
            {d.descartados.map((x, i) => (
              <div key={i}>
                <p><b>{x.que}</b></p>
                {x.por_que && <p className="vacio">{x.por_que}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {caps.length > 0 && (
        <section className="carta">
          <h2>Mapa de capítulos<span className="sub">largo de cada uno, en palabras</span></h2>
          {/* 17-sep: con un documento sin capítulos fiables el lector lo lee por tramos, y se dice. */}
          {caps.every((c) => /^#*\s*Tramo\b/.test(c.title)) && (
            <p className="vacio">Tu documento no trae capítulos que se puedan reconocer, así que se ha leído por tramos del mismo tamaño.</p>
          )}
          <div className="mapa">
            {caps.map((c) => (
              <div className="cap" key={c.n} title={c.resumen ?? tituloDeCapitulo(c.title)}>
                <span className="idx mono">{c.n}</span>
                <span className="tit">{tituloDeCapitulo(c.title)}</span>
                <span className="raya"><i style={{ width: `${(c.palabras / masLargo) * 100}%` }} /></span>
                <b className="mono">{miles(c.palabras)}</b>
              </div>
            ))}
          </div>
        </section>
      )}

      {issues.length > 0 && (
        <section className="carta">
          <h2>Qué arreglar<span className="sub">{issues.length} señalados, por orden de impacto</span></h2>
          <div className="issues">
            {["alta", "media", "baja"].flatMap((s) =>
              issues.filter((i) => i.severidad === s).map((i, n) => {
                const clave = s + n;
                const p = verPasaje === clave ? pasajeDe(i) : null;
                const puesto = alPlan?.clave === clave ? alPlan.estado : null;
                return (
                <div className={"issue " + s} key={clave}>
                  <span className={"grado " + s}>{s === "alta" ? "Importante" : s === "media" ? "Medio" : "Menor"}</span>
                  <blockquote>{i.excerpt}</blockquote>
                  <p><b>{i.problema}</b></p>
                  <p className="vacio">{i.arreglo}</p>
                  {/* Al pasaje y al plan: un hallazgo que no lleva a ningún sitio es una queja. */}
                  <div className="botones">
                    <button className="chip" disabled={!doc || abriendo}
                            onClick={async () => { if (verPasaje === clave) { setVerPasaje(null); return; } await traerLibro(); setVerPasaje(clave); }}>
                      {abriendo && verPasaje !== clave ? "Abriendo el manuscrito…" : verPasaje === clave ? "Ocultar el pasaje" : "Ver el pasaje"}
                    </button>
                    {plan
                      ? <button className="chip" disabled={puesto === "yendo"} onClick={() => añadirAlPlan(clave, i)}>
                          {puesto === "yendo" ? "Añadiendo…" : puesto === "hecho" ? "✓ En el plan" : "Añadir al plan"}
                        </button>
                      : alIr && <button className="chip" onClick={() => alIr("plan")}>Prepara el plan para añadirlo</button>}
                    {puesto === "hecho" && alIr && <button className="btn texto" onClick={() => alIr("plan")}>Ver el plan</button>}
                  </div>
                  {puesto && puesto !== "yendo" && puesto !== "hecho" && <p className="vacio" style={{ color: "var(--acc)" }}>{puesto}</p>}
                  {verPasaje === clave && (
                    p
                      ? <div className="hoja pasaje">
                          {p.tituloCapitulo && <p className="vacio">{tituloDeCapitulo(p.tituloCapitulo)}</p>}
                          <span className="vacio">…{p.antes}</span><mark>{p.cita}</mark><span className="vacio">{p.despues}…</span>
                        </div>
                      : <p className="vacio">No encuentro esa cita en {doc ? nombreDeRuta(doc) : "el manuscrito"}: puede que ya la hayas cambiado, o que el informe sea de otra versión.</p>
                  )}
                </div>
              ); }))}
          </div>
        </section>
      )}
    </>
  );
}
